import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Pencil, Star, AlertCircle } from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  WorkflowTemplate,
  createWorkflowTemplate,
  deleteWorkflowTemplate,
  listWorkflowTemplates,
  updateWorkflowTemplate,
} from "@/services/workflowTemplatesApi";
import { useToast } from "@/hooks/use-toast";
import { confirmDialog } from "@/components/ui/confirm-dialog";

const DEFAULT_CONFIG_SKELETON = JSON.stringify(
  {
    branches: {
      enrichment: true,
      scoring: true,
      human_validation_before_invite: false,
      invite_phase: true,
      message_sequence: true,
      reply_classification: true,
      precision_loop: true,
      not_interested_followup: false,
      long_reconnect_6_months: true,
      booking_flow: true,
      appointment_polling: true,
    },
    delays: {
      before_first_message_days: 2,
      between_messages_days: [2, 2, 3, 5, 3, 7, 1, 14],
      invite_acceptance_check_days: 2,
      precision_wait_days: 3,
      appointment_polling_days: [5, 10, 15, 20],
      long_reconnect_months: 6,
    },
    quotas: {
      daily_invites_per_campaign: null,
      daily_messages_per_campaign: null,
      send_window: null,
    },
    scoring: { min_score: 0.0, require_all_filters_pass: false },
    booking: { jwt_expires_in_days: 7 },
  },
  null,
  2,
);

export default function WorkflowTemplatesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [templates, setTemplates] = useState<WorkflowTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newConfig, setNewConfig] = useState(DEFAULT_CONFIG_SKELETON);
  const [newIsDefault, setNewIsDefault] = useState(false);
  const [newIsActive, setNewIsActive] = useState(true);
  const [createError, setCreateError] = useState<string | null>(null);

  // edit dialog
  const [editing, setEditing] = useState<WorkflowTemplate | null>(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editConfig, setEditConfig] = useState("");
  const [editIsDefault, setEditIsDefault] = useState(false);
  const [editIsActive, setEditIsActive] = useState(true);
  const [editError, setEditError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listWorkflowTemplates(navigate);
      setTemplates(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const resetCreateForm = () => {
    setNewName("");
    setNewDescription("");
    setNewConfig(DEFAULT_CONFIG_SKELETON);
    setNewIsDefault(false);
    setNewIsActive(true);
    setCreateError(null);
  };

  const handleCreate = async () => {
    setCreateError(null);
    if (!newName.trim()) {
      setCreateError("Le nom est requis.");
      return;
    }
    let parsedConfig: Record<string, unknown>;
    try {
      parsedConfig = JSON.parse(newConfig);
      if (typeof parsedConfig !== "object" || parsedConfig === null) {
        throw new Error("La config doit etre un objet JSON.");
      }
    } catch (e) {
      setCreateError(
        "JSON invalide : " + (e instanceof Error ? e.message : "format incorrect"),
      );
      return;
    }
    try {
      await createWorkflowTemplate(
        {
          name: newName.trim(),
          description: newDescription.trim() || null,
          config: parsedConfig,
          is_default: newIsDefault,
          is_active: newIsActive,
        },
        navigate,
      );
      setCreateOpen(false);
      resetCreateForm();
      void refresh();
    } catch (e) {
      setCreateError(e instanceof Error ? e.message : "Erreur de creation");
    }
  };

  const openEdit = (tpl: WorkflowTemplate) => {
    setEditing(tpl);
    setEditName(tpl.name);
    setEditDescription(tpl.description ?? "");
    setEditConfig(JSON.stringify(tpl.config, null, 2));
    setEditIsDefault(tpl.is_default);
    setEditIsActive(tpl.is_active);
    setEditError(null);
  };

  const handleSaveEdit = async () => {
    if (!editing) return;
    setEditError(null);
    let parsedConfig: Record<string, unknown> | null;
    try {
      parsedConfig = JSON.parse(editConfig);
      if (typeof parsedConfig !== "object" || parsedConfig === null) {
        throw new Error("La config doit etre un objet JSON.");
      }
    } catch (e) {
      setEditError(
        "JSON invalide : " + (e instanceof Error ? e.message : "format incorrect"),
      );
      return;
    }
    try {
      await updateWorkflowTemplate(
        editing.id,
        {
          name: editName.trim(),
          description: editDescription.trim() || null,
          config: parsedConfig,
          is_default: editIsDefault,
          is_active: editIsActive,
        },
        navigate,
      );
      setEditing(null);
      void refresh();
    } catch (e) {
      setEditError(e instanceof Error ? e.message : "Erreur de sauvegarde");
    }
  };

  const handleDelete = async (tpl: WorkflowTemplate) => {
    if (tpl.is_default) {
      toast({
        title: "Action impossible",
        description: "Impossible de supprimer le template par defaut.",
        variant: "destructive",
      });
      return;
    }
    const msg =
      tpl.campaigns_count > 0
        ? `Ce template est utilisé par ${tpl.campaigns_count} campagne(s). La suppression le désactivera (soft delete).`
        : `Le template "${tpl.name}" sera définitivement supprimé.`;
    const ok = await confirmDialog({
      title: "Supprimer le template",
      description: msg,
      confirmLabel: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    try {
      await deleteWorkflowTemplate(tpl.id, navigate);
      void refresh();
    } catch (e) {
      toast({
        title: "Erreur de suppression",
        description: e instanceof Error ? e.message : "Erreur de suppression",
        variant: "destructive",
      });
    }
  };

  const handleSetDefault = async (tpl: WorkflowTemplate) => {
    if (tpl.is_default) return;
    try {
      await updateWorkflowTemplate(tpl.id, { is_default: true }, navigate);
      void refresh();
    } catch (e) {
      toast({
        title: "Erreur",
        description: e instanceof Error ? e.message : "Erreur",
        variant: "destructive",
      });
    }
  };

  const sortedTemplates = useMemo(
    () =>
      [...templates].sort((a, b) => {
        if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
        if (a.is_active !== b.is_active) return a.is_active ? -1 : 1;
        return a.name.localeCompare(b.name);
      }),
    [templates],
  );

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Templates de workflow</h1>
          </div>
        </header>

        <div className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Gerer les templates preconfigures (Chasse standard, Chasse douce, ...).
              Les recruteurs choisissent parmi ces templates et peuvent les
              amender via overrides sur leurs campagnes.
            </p>
            <Dialog open={createOpen} onOpenChange={(o) => {
              setCreateOpen(o);
              if (!o) resetCreateForm();
            }}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Nouveau template
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl">
                <DialogHeader>
                  <DialogTitle>Creer un template de workflow</DialogTitle>
                  <DialogDescription>
                    Le champ `config` doit etre un JSON conforme au schema
                    WorkflowConfig (branches/delays/quotas/scoring/booking).
                    Valeurs manquantes = defauts de la Chasse standard.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-3 py-2">
                  <div className="space-y-1">
                    <Label htmlFor="wf-name">Nom</Label>
                    <Input
                      id="wf-name"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="ex: Chasse ciblee executives"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="wf-desc">Description</Label>
                    <Input
                      id="wf-desc"
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                      placeholder="A qui / quand utiliser ce template ?"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="wf-config">Config JSON</Label>
                    <Textarea
                      id="wf-config"
                      value={newConfig}
                      onChange={(e) => setNewConfig(e.target.value)}
                      rows={16}
                      className="font-mono text-xs"
                    />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <Label>Template par defaut</Label>
                      <p className="text-xs text-muted-foreground">
                        Le template utilise pour toute nouvelle campagne sans
                        selection explicite.
                      </p>
                    </div>
                    <Switch checked={newIsDefault} onCheckedChange={setNewIsDefault} />
                  </div>
                  <div className="flex items-center justify-between rounded-md border p-3">
                    <div>
                      <Label>Actif</Label>
                      <p className="text-xs text-muted-foreground">
                        Visible dans le dropdown du recruteur.
                      </p>
                    </div>
                    <Switch checked={newIsActive} onCheckedChange={setNewIsActive} />
                  </div>
                  {createError && (
                    <div className="flex items-center gap-2 text-sm text-destructive">
                      <AlertCircle className="h-4 w-4" />
                      {createError}
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setCreateOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleCreate} disabled={!newName.trim()}>
                    Creer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}

          {loading ? (
            <div className="text-sm text-muted-foreground">Chargement...</div>
          ) : sortedTemplates.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              Aucun template. Creez-en un ci-dessus.
            </div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className="w-[120px]">Statut</TableHead>
                    <TableHead className="w-[120px]">Campagnes</TableHead>
                    <TableHead className="w-[240px] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sortedTemplates.map((tpl) => (
                    <TableRow key={tpl.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {tpl.is_default && (
                            <Badge variant="default" className="text-[10px]">
                              <Star className="mr-1 h-3 w-3" /> defaut
                            </Badge>
                          )}
                          {tpl.name}
                        </div>
                      </TableCell>
                      <TableCell className="max-w-md text-sm text-muted-foreground">
                        <div className="truncate">{tpl.description || "-"}</div>
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant={tpl.is_active ? "default" : "secondary"}
                          className="text-[10px]"
                        >
                          {tpl.is_active ? "actif" : "inactif"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm">{tpl.campaigns_count}</TableCell>
                      <TableCell className="space-x-2 text-right">
                        {!tpl.is_default && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSetDefault(tpl)}
                            title="Definir comme defaut"
                          >
                            <Star className="h-3 w-3" />
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEdit(tpl)}
                        >
                          <Pencil className="mr-2 h-3 w-3" />
                          Editer
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(tpl)}
                          aria-label="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        {/* Edit dialog */}
        <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle>
                Editer le template{" "}
                <span className="font-mono text-sm">{editing?.name}</span>
              </DialogTitle>
              <DialogDescription>
                {editing?.campaigns_count ? (
                  <>
                    Ce template est utilise par {editing.campaigns_count}{" "}
                    campagne(s). Les modifications de config impacteront les
                    prochaines executions n8n (les campagnes en cours gardent
                    leur snapshot sauf reexecution).
                  </>
                ) : (
                  "Ce template n'est utilise par aucune campagne."
                )}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1">
                <Label htmlFor="wf-edit-name">Nom</Label>
                <Input
                  id="wf-edit-name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="wf-edit-desc">Description</Label>
                <Input
                  id="wf-edit-desc"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="wf-edit-config">Config JSON</Label>
                <Textarea
                  id="wf-edit-config"
                  value={editConfig}
                  onChange={(e) => setEditConfig(e.target.value)}
                  rows={16}
                  className="font-mono text-xs"
                />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Template par defaut</Label>
                  <p className="text-xs text-muted-foreground">
                    Ne peut etre desactive que si un autre template actif devient
                    defaut.
                  </p>
                </div>
                <Switch checked={editIsDefault} onCheckedChange={setEditIsDefault} />
              </div>
              <div className="flex items-center justify-between rounded-md border p-3">
                <div>
                  <Label>Actif</Label>
                  <p className="text-xs text-muted-foreground">
                    Desactiver retire du dropdown. Les campagnes existantes
                    continuent d'utiliser leur snapshot.
                  </p>
                </div>
                <Switch checked={editIsActive} onCheckedChange={setEditIsActive} />
              </div>
              {editError && (
                <div className="flex items-center gap-2 text-sm text-destructive">
                  <AlertCircle className="h-4 w-4" />
                  {editError}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setEditing(null)}>
                Annuler
              </Button>
              <Button onClick={handleSaveEdit}>Enregistrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SidebarInset>
    </SidebarProvider>
  );
}
